import { createContext, useContext } from 'react'

import {
  JourneyOutcome,
  JourneyTerminationReason,
} from '@cash-web/protos/squareup/cash/blockuserjourneys/api/v1/service.pb'
import { Listener } from '@cash-web/shared-data-access-redux-inspect'

import { UserJourneyAction } from './types'

export type UserJourneyListener = Listener<UserJourneyState, UserJourneyAction>

export type UserJourney = {
  /** Unique identifier for this specific journey instance */
  id: string
  /** Human-readable name of the journey (e.g., "login", "payment", "onboarding") */
  name: string
  /** Timestamp in milliseconds when the journey was started (Date.now()) */
  created: number
  /** Single variant identifier for A/B testing or feature flags that affected this journey */
  variant?: string
  /** Array of frustration signals - known bad events that indicate poor user experience (e.g., "server-error", "timeout") */
  frustrations: string[]
  /** Array of friction signals - potentially problematic events that may indicate issues at scale (e.g., "card-declined", "retry-required") */
  frictions: string[]
  /** Array of custom tags for additional journey metadata and categorization */
  tags: string[]
}

/**
 * The state of the user journey, including the history of all journeys and the currently active journey.
 */
export type UserJourneyState = {
  /** The history of all journeys */
  history: string[]
  /** journey name to journey state */
  journeys: Record<string, UserJourney>
  /** The currently active journey */
  active?: string
}

/**
 * The context for the user journey, including the functions to start, complete, and add to the journey.
 */
export type UserJourneyContextProps = {
  /** Starts a new journey with the given name */
  startJourney: (name: string) => void
  /** Completes the current journey with the given outcome and termination reason */
  completeJourney: (name: string, outcome: JourneyOutcome, terminationReason?: JourneyTerminationReason) => void
  /** Adds a frustration signal to the current journey */
  addFrustration: (name: string, frustration: string) => void
  /** Adds a friction signal to the current journey */
  addFriction: (name: string, friction: string) => void
  /** Adds a tag to the current journey */
  addTag: (name: string, tag: string) => void
  /** Sets the variant for the journey */
  setVariant: (name: string, variant: string) => void
  /** Subscribes to the user journey state changes */
  subscribe: (listener: UserJourneyListener) => () => void
  /** The currently active journey */
  active?: UserJourney
  /** The history of all journeys */
  history?: UserJourney[]
}

const noop = () => undefined

const defaultContext = {
  startJourney: noop,
  completeJourney: noop,
  addFrustration: noop,
  addFriction: noop,
  addTag: noop,
  setVariant: noop,
  subscribe: () => noop,
}

export const UserJourneyContext = createContext<UserJourneyContextProps>(defaultContext)

export const useUserJourneyContext = () => {
  return useContext(UserJourneyContext)
}
